import { Badge, Group, Text } from '@mantine/core';
import {
  IconCalendar,
  IconCalendarCheck,
  IconClock,
  IconCurrencyEuro,
  IconEdit,
  IconTrash,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ProjectComments } from 'src/Components/Comments';
import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { KeyValueLine } from 'src/Components/Shared/KeyValueLine';
import { RowActions } from 'src/Components/Shared/RowActions';
import { ShareToWhatsAppButton } from 'src/Components/Shared/ShareToWhatsAppButton';
import { SlateRenderer } from 'src/Components/Shared/SlateRenderer';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import useDeleteProject from 'src/Hooks/Mutations/useDeleteProject';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchProjects from 'src/Hooks/Queries/useFetchProjects';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { CreateProjectDtoStatusEnum, ProjectDtoPriorityEnum } from 'src/Services/API/generated/api';

import styles from './PreviewProjectDrawer.module.scss';

const PRIORITY_CONFIG: Record<ProjectDtoPriorityEnum, { color: string; label: string }> = {
  [ProjectDtoPriorityEnum.High]: { color: 'red', label: 'Haute' },
  [ProjectDtoPriorityEnum.Medium]: { color: 'orange', label: 'Moyenne' },
  [ProjectDtoPriorityEnum.Low]: { color: 'green', label: 'Basse' },
};

const parseDescription = (description: string | undefined): SlateNode[] | null => {
  if (!description) return null;
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) return parsed;
    return null;
  } catch {
    // Legacy plain text - wrap in paragraph
    return [{ type: 'paragraph', children: [{ text: description }] }];
  }
};

const PreviewProjectDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId, projectId } = useParams<{ assetId: string; projectId: string }>();
  const isFromPlanning = location.pathname.includes('/planning/');
  const closeTo = isFromPlanning
    ? `/asset/${assetId}/planning`
    : `/asset/${assetId}/project-and-works`;
  const { data: projects, isLoading: isLoadingProjects } = useFetchProjects(assetId!);
  const { data: assetDetails } = useFetchAssetDetails();
  const { mutate: deleteProject, isLoading: isDeleting } = useDeleteProject(assetId!);

  const project = projects?.find(p => p.id === projectId);

  useEffect(() => {
    if (!isLoadingProjects && projects && !project) {
      navigate(closeTo, { replace: true });
    }
  }, [isLoadingProjects, projects, project, navigate, closeTo]);

  if (!project) return null;

  const creator = project.createdBy;

  const handleDelete = () => {
    if (
      window.confirm(t('common.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce projet ?'))
    ) {
      deleteProject(projectId!);
      navigate(closeTo);
    }
  };

  const getStatusLabel = (status: CreateProjectDtoStatusEnum) => {
    switch (status) {
      case CreateProjectDtoStatusEnum.IdeaBox:
        return t('projects.status.ideaBox', 'Boîte à idées');
      case CreateProjectDtoStatusEnum.IdeaToVote:
        return t('projects.status.ideaToVote', 'À voter');
      case CreateProjectDtoStatusEnum.ProjectValidated:
        return t('projects.status.projectValidated', 'Validé');
      case CreateProjectDtoStatusEnum.ProjectInProgress:
        return t('projects.status.projectInProgress', 'En cours');
      case CreateProjectDtoStatusEnum.ProjectCompleted:
        return t('projects.status.projectCompleted', 'Terminé');
      default:
        return status;
    }
  };

  const getDurationUnitLabel = (unit: string) => {
    switch (unit) {
      case 'DAYS':
        return t('common.days', 'Jours');
      case 'WEEKS':
        return t('common.weeks', 'Semaines');
      case 'MONTHS':
        return t('common.months', 'Mois');
      case 'YEARS':
        return t('common.years', 'Années');
      default:
        return unit;
    }
  };

  return (
    <AnimatedRoutedDrawer title={t('projects.preview.title', 'Détails du projet')} to={closeTo}>
      {() => (
        <div className={styles.container}>
          <div className={styles.header}>
            <div>
              <div className={styles.title}>{project.title}</div>
              <Group gap="xs">
                <Badge color="blue" variant="light">
                  {getStatusLabel(project.status!)}
                </Badge>
                {project.priority && (
                  <Badge color={PRIORITY_CONFIG[project.priority].color} variant="light">
                    {t(
                      `projects.priority.${project.priority.toLowerCase()}`,
                      PRIORITY_CONFIG[project.priority].label
                    )}
                  </Badge>
                )}
              </Group>
            </div>
            <Group gap="xs">
              <ShareToWhatsAppButton
                assetName={assetDetails?.name ?? ''}
                excerpt={project.title}
                target={{
                  kind: 'project',
                  assetId: assetId!,
                  projectId: project.id,
                }}
                trackingSource={'project'}
              />
              <RowActions
                actions={[
                  {
                    key: 'edit',
                    label: t('common.edit', 'Modifier'),
                    icon: <IconEdit size={18} />,
                    onClick: () => navigate('edit', { replace: true }),
                  },
                  {
                    key: 'delete',
                    label: t('common.delete', 'Supprimer'),
                    icon: <IconTrash size={18} />,
                    variant: 'danger',
                    disabled: isDeleting,
                    onClick: handleDelete,
                  },
                ]}
              />
            </Group>
          </div>

          {creator && (
            <Group gap="xs">
              <UserAvatar user={creator} size="sm" />
              <Text size="sm" c="dimmed">
                {t('projects.preview.createdBy', 'Proposé par')}{' '}
                <strong>
                  {creator.firstName} {creator.lastName}
                </strong>
              </Text>
            </Group>
          )}

          {project.description && parseDescription(project.description) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('common.description', 'Description')}</div>
              <div className={styles.description}>
                <SlateRenderer value={parseDescription(project.description)!} />
              </div>
            </div>
          )}

          {project.members?.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {t('projects.form.responsibles', 'Responsable(s) du projet')}
              </div>
              <Group gap="xs">
                {project.members.map(member => (
                  <Group key={member.id} gap="xs">
                    <UserAvatar user={member} size="sm" />
                    <Text size="sm">
                      {member.firstName} {member.lastName}
                    </Text>
                  </Group>
                ))}
              </Group>
            </div>
          )}

          {project.participants?.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {t('projects.form.participants', 'Participant(s) au projet')}
              </div>
              <Group gap="xs">
                {project.participants.map(participant => (
                  <Group key={participant.id} gap="xs">
                    <UserAvatar user={participant} size="sm" />
                    <Text size="sm">
                      {participant.firstName} {participant.lastName}
                    </Text>
                  </Group>
                ))}
              </Group>
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('common.details', 'Détails')}</div>
            <KeyValueLine
              propertyKey={t('projects.form.completionDate', 'Date de réalisation')}
              value={
                project.completionDate
                  ? dayjs(project.completionDate).toDate().toLocaleDateString()
                  : '-'
              }
              keyIcon={<IconCalendarCheck size={16} />}
            />
            <KeyValueLine
              propertyKey={t('projects.create.deadline', 'Date limite')}
              value={project.deadline ? dayjs(project.deadline).toDate().toLocaleDateString() : '-'}
              keyIcon={<IconCalendar size={16} />}
            />
            <KeyValueLine
              propertyKey={t('projects.preview.estimation', 'Estimation')}
              value={
                project.estimatedCost || project.estimatedDuration ? (
                  <Group gap="xs">
                    {project.estimatedCost && (
                      <Group gap={4}>
                        <IconCurrencyEuro size={14} /> {project.estimatedCost}
                      </Group>
                    )}
                    {project.estimatedDuration && (
                      <Group gap={4}>
                        <IconClock size={14} /> {project.estimatedDuration}{' '}
                        {getDurationUnitLabel(project.estimatedDurationUnit!)}
                      </Group>
                    )}
                  </Group>
                ) : (
                  '-'
                )
              }
              keyIcon={<IconClock size={16} />}
            />
          </div>

          <LinkedEntitiesSection
            entityType={LinkableEntityType.Project}
            entityId={project.id}
            entityTitle={project.title}
          />

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('common.comments', 'Commentaires')}</div>
            <ProjectComments projectId={projectId!} assetId={assetId!} />
          </div>
        </div>
      )}
    </AnimatedRoutedDrawer>
  );
};

export default PreviewProjectDrawer;
